import Vue from 'vue';
import VueI18n, { LocaleMessages } from 'vue-i18n';
import appLocales from '@/locales';
import vue2UiLocales from '@gms/vue2-ui/src/locales';
import languageLocales from '@gms/language/src/locales'
import { Languages } from '@gms/language/src/model/language';
import { defaultDateTimeFormats, defaultNumberFormats } from '@gms/language/src/formats';
import { createI18n } from './composables/useI18n';

Vue.use(VueI18n);

function mergeLocales(): LocaleMessages {
  // Get all available locales from imported locale files. The index.ts files in each locale folder
  // export an object containing all locales ({ de, en, fr, it, ...}). This way, we can get all
  // supported locales
  const supportedLocales = new Set([
    ...Object.keys(appLocales),
    ...Object.keys(vue2UiLocales),
    ...Object.keys(languageLocales),
  ])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const messages: Record<string, any> = {}

  supportedLocales.forEach((locale: string) => {
    messages[locale] = {
      ...(vue2UiLocales[locale as keyof typeof vue2UiLocales] || {}),
      ...(languageLocales[locale as keyof typeof languageLocales] || {}),
      // App overrides libraries
      ...(appLocales[locale as keyof typeof appLocales] || {}),
    }
  })

  return messages
}

function getBrowserLocale() {
  const navigatorLocale = navigator.languages !== undefined
    ? navigator.languages[0]
    : navigator.language

  if (!navigatorLocale) {
    return undefined
  }

  const trimmedLocale = navigatorLocale.trim().split(/-|_/)[0]

  return trimmedLocale
}

function supportedLocalesInclude(locale: any) {
  return Object.values(Languages).includes(locale)
}

export function getStartingLocale() {
  const browserLocale = getBrowserLocale()
  if (browserLocale && supportedLocalesInclude(browserLocale)) {
    return browserLocale
  }
  return process.env.VUE_APP_I18N_LOCALE || 'en'
}

export default createI18n({
  locale: getStartingLocale(),
  fallbackLocale: 'en',
  formatFallbackMessages: true,
  messages: mergeLocales(),
  silentFallbackWarn: true,
  silentTranslationWarn: true,
  dateTimeFormats: defaultDateTimeFormats,
  numberFormats: defaultNumberFormats,
});
